/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, June 2023
 * @flow
 */

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cn from 'classnames';
import useStyles from './useStyles';
import { Typography, Button, IconButton, Box } from '@material-ui/core';
import { HighlightOff as HighlightOffIcon } from '@material-ui/icons';
import { CountryPicker, getPickerData } from "../../../../components/CountryPicker";
import FireboardUtils from '../../../../util/fireboard-util';
import TextInput from '../TextInput';

const EmailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const defaultInputFields = {
  nameEnabled: true,
  nameRequired: true,
  phoneNumberEnabled: true,
  phoneNumberRequired: true,
  emailAddressEnabled: true,
  emailAddressRequired: true,
};

const EditWaitList = ({ booking, goToWaitlistScreen, onUpdateBooking }) => {
  const classes = useStyles();
  const [selectedCountry, setSelectedCountry] = useState({});
  const [state, setState] = useState({
    phoneNumber: '',
    name: '',
    emailAddress: '',
    partySize: '',
    note: '',
    comment: '',
  });
  const { labelsData } = useSelector(state => state);
  const { labels: strings } = labelsData;

  const { storeInfo, deviceApi, currencyInfo } = useSelector(state => state);
  const { deviceGroup } = deviceApi?.data || {};
  const {
    nameEnabled,
    nameRequired,
    phoneNumberEnabled,
    phoneNumberRequired,
    emailAddressEnabled,
    emailAddressRequired,
  } = deviceGroup?.inputFields || defaultInputFields;
  const { iso2 } = currencyInfo || {};

  useEffect(() => {
    if (Object.keys(booking).length) {
      const countriesData = getPickerData();
      let defaultCountry = {};
      if (booking?.countryCode) {
        const dialCode = `+${booking.countryCode}`;
        defaultCountry = countriesData.find(country => country.dialCode === dialCode) || {};
      } else {
        defaultCountry = countriesData.find(country => country.iso2 === String(iso2).toLowerCase()) || {};
      }
      if (Object.keys(defaultCountry).length === 0) {
        defaultCountry = {
          dialCode: "+1",
          image: "/static/media/us.e4a75107.png",
          iso2: "us",
          key: 0,
          label: "United States"
        };
      }
      const formattedPhoneNumber = FireboardUtils.inputFormattedPhnNum(booking.phoneNumber, defaultCountry.iso2);
      setState({
        ...state,
        phoneNumber: formattedPhoneNumber,
        name: booking.name,
        emailAddress: booking.emailAddress,
        partySize: String(booking.partySize),
        note: booking.note,
        comment: booking.comment,
      });
      setSelectedCountry(defaultCountry);
    }
  }, [booking]);

  const onCountryChange = country => {
    const formattedPhoneNumber = FireboardUtils.inputFormattedPhnNum(state.phoneNumber, country.iso2);
    setSelectedCountry(country);
    setState({
      ...state,
      phoneNumber: formattedPhoneNumber,
    });
  };

  const onChangeInput = (key, value) => {
    if (key === 'note') {
      setState({
        ...state,
        [key]: document.getElementById("noteArea").value,
      })
    } else if (key === 'comment') {
      setState({
        ...state,
        [key]: document.getElementById("staffCommentArea").value,
      })
    } else {
      setState({
        ...state,
        [key]: value,
      })
    }
  };

  const onSubmit = () => {
    try {
      const { id: storeId } = storeInfo?.data || {};
      const payload = {
        storeId,
        countryCode: state.phoneNumber ? selectedCountry.dialCode.split('+')[1] : '',
        phoneNumber: state.phoneNumber ? FireboardUtils.inputUnFormattedPhnNum(state.phoneNumber, selectedCountry.iso2) : '',
        name: state.name.trim() || '',
        emailAddress: state.emailAddress.trim() || '',
        note: state.note.trim() || '',
        comment: state.comment.trim() || '',
        partySize: +state.partySize,
        businessDate: booking.businessDate,
        bookingDate: booking.bookingDate,
        completeDate: booking.completeDate,
        status: booking.status,
        stationId: booking.stationId,
        memberId: booking.memberId,
        id: booking.id,
      };
      onUpdateBooking && onUpdateBooking(payload);
    } catch (error) {
      console.error(error);
    }
  };

  const isSubmitDisabled = () => {
    let isEmailValid = true, isNameValid = true, isPhoneNumberValid = true;
    if (emailAddressEnabled && (emailAddressRequired || (!emailAddressRequired && state.emailAddress))) {
      isEmailValid = EmailRegex.test(state.emailAddress.trim().toLowerCase());
    }
    if (nameEnabled && (nameRequired || (!nameRequired && state.name))) {
      isNameValid = ((state.name).trim()).length > 0;
    }
    if (phoneNumberEnabled && (phoneNumberRequired || (!phoneNumberRequired && state.phoneNumber))) {
      isPhoneNumberValid = (FireboardUtils.inputUnFormattedPhnNum(state.phoneNumber, selectedCountry.iso2)).length >= 10;
    }
    const isPartySizeValid = state.partySize.length > 0;
    return !(isEmailValid && isNameValid && isPhoneNumberValid && isPartySizeValid);
  };

  const isInputDisabled = () => {
    return booking?.status === 7 || booking?.status === 9;
  };

  return (
    <Box height={'100vh'} overflow={'scroll'} className={classes.container}>
      <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} className={classes.header}>
        <Typography variant='h6' className={classes.title}>{strings.CREATE_WAITLIST_UPDATE_WAITLIST}</Typography>
        <IconButton className={classes.closeImg} onClick={goToWaitlistScreen}>
          <HighlightOffIcon />
        </IconButton>
      </Box>
      <Box padding={2}>
        {nameEnabled && (
          <TextInput
            placeholder={`${strings.CREATE_WAITLIST_NAME} ${nameRequired ? '' : `(${strings.CREATE_WAITLIST_OPTIONAL})`}`}
            type='text'
            value={state.name}
            onChange={value => onChangeInput('name', value)}
            disabled={isInputDisabled()}
            inputStyle={classes.input}
            labelStyle={classes.label}
          />
        )}
        <TextInput
          placeholder={strings.CREATE_WAITLIST_PARTY_SIZE}
          value={state.partySize}
          type='number'
          onChange={value => onChangeInput('partySize', value)}
          inputStyle={classes.input}
          labelStyle={classes.label}
          disabled={isInputDisabled()}
        />
        {phoneNumberEnabled && (
          <React.Fragment>
            <Typography variant='body2' className={classes.label}>{`${strings.CREATE_WAITLIST_PHONE}${phoneNumberRequired ? '*' : `(${strings.CREATE_WAITLIST_OPTIONAL})`}`}</Typography>
            <Box display={'flex'} flexDirection={'row'} justifyContent={'space-around'} marginBottom={1.2}>
              <Box width={'60%'}>
                <CountryPicker
                  selectedCountry={selectedCountry}
                  onCountryChange={isInputDisabled() ? null : onCountryChange}
                  containerStyle={cn(classes.input, classes.contryInput)}
                />
              </Box>
              <Box width={'40%'}>
                <input
                  type='tel'
                  value={FireboardUtils.inputFormattedPhnNum(state.phoneNumber, selectedCountry.iso2)}
                  onChange={event => onChangeInput('phoneNumber', event.target.value)}
                  className={classes.input}
                  disabled={isInputDisabled()}
                />
              </Box>
            </Box>
          </React.Fragment>
        )}
        {emailAddressEnabled && (
          <TextInput
            placeholder={`${strings.CREATE_WAITLIST_EMAIL}${emailAddressRequired ? '*' : `(${strings.CREATE_WAITLIST_OPTIONAL})`}`}
            value={state.emailAddress}
            type='text'
            onChange={value => onChangeInput('emailAddress', value)}
            disabled={isInputDisabled()}
            inputStyle={classes.input}
            labelStyle={classes.label}
          />
        )}
        <Box display={'flex'} flexDirection={'column'}>
          <Typography variant='body2' className={classes.label}>{`${strings.CREATE_WAITLIST_NOTE} (${strings.CREATE_WAITLIST_OPTIONAL})`}</Typography>
          <textarea
            rows={3}
            maxLength={100}
            id="noteArea"
            value={state.note}
            onChange={value => onChangeInput('note', value)}
            className={classes.textarea}
          ></textarea>
        </Box>
        <Box display={'flex'} flexDirection={'column'}>
          <Typography variant='body2' className={classes.label}>{`${strings.CREATE_WAITLIST_STAFF_COMMENT} (${strings.CREATE_WAITLIST_OPTIONAL})`}</Typography>
          <textarea
            rows={3}
            maxLength={100}
            id="staffCommentArea"
            value={state.comment}
            onChange={value => onChangeInput('comment', value)}
            className={classes.textarea}
          ></textarea>
        </Box>
      </Box>
      <Box display={'flex'} flexDirection={'column'} padding={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={onSubmit}
          className={classes.button}
          disabled={isSubmitDisabled()}
        >
          {strings.CREATE_WAITLIST_UPDATE_WAITLIST}
        </Button>
        <Button
          variant="outlined"
          color='primary'
          onClick={goToWaitlistScreen}
          className={classes.secondaryButton}
        >
          {strings.CREATE_WAITLIST_CANCEL}
        </Button>
      </Box>
    </Box>
  );
};

export default EditWaitList;

EditWaitList.propTypes = {
  booking: PropTypes.object,
  goToWaitlistScreen: PropTypes.func,
  onCreateBooking: PropTypes.func,
};

EditWaitList.defaultProps = {
  booking: {},
  goToWaitlistScreen: () => { },
  onCreateBooking: () => { },
};
